import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'

const ENTITY_LABELS = { '1040':'1040 Individual','1120S':'S-Corp','1065':'Partnership','1120':'C-Corp','schedule_c':'Schedule C','990':'Nonprofit' }

function Modal({ title, onClose, children }) {
  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:24}}
      onClick={e=>{if(e.target===e.currentTarget)onClose()}}>
      <div className="card animate-fadeUp" style={{width:'100%',maxWidth:520,maxHeight:'92vh',overflow:'auto',padding:0}}>
        <div style={{padding:'18px 22px',borderBottom:'1px solid var(--border)',display:'flex',justifyContent:'space-between',alignItems:'center',position:'sticky',top:0,background:'#fff',zIndex:10}}>
          <h2 style={{fontSize:17}}>{title}</h2>
          <button onClick={onClose} style={{background:'none',border:'none',fontSize:20,color:'#62615D',cursor:'pointer'}}>×</button>
        </div>
        <div style={{padding:22}}>{children}</div>
      </div>
    </div>
  )
}

export default function ContactsPage() {
  const { profile } = useAuth()
  const [contacts, setContacts] = useState([])
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState(null)
  const [selectedContact, setSelectedContact] = useState(null)
  const [showLinkModal, setShowLinkModal] = useState(false)
  const [toast, setToast] = useState('')
  const [form, setForm] = useState({ first_name:'', last_name:'', email:'', phone:'', address:'', city:'', state:'NC', zip:'', notes:'' })

  const notify = msg => { setToast(msg); setTimeout(()=>setToast(''),3000) }
  const set = k => e => setForm(f=>({...f,[k]:e.target.value}))

  useEffect(()=>{ load() },[])

  async function load() {
    const { data:c } = await supabase.from('contacts').select('*').eq('cpa_id',profile.id).order('last_name')
    const { data:cl } = await supabase.from('clients').select('*').eq('cpa_id',profile.id).order('business_name')
    setContacts(c||[])
    setClients(cl||[])
    setLoading(false)
    // Refresh selectedContact so linked entities update immediately
    setSelectedContact(prev => prev ? (c||[]).find(x => x.id === prev.id) || prev : null)
  }

  function openNew() {
    setEditing(null)
    setForm({ first_name:'', last_name:'', email:'', phone:'', address:'', city:'', state:'NC', zip:'', notes:'' })
    setShowModal(true)
  }

  function openEdit(contact) {
    setEditing(contact)
    setForm({ first_name:contact.first_name, last_name:contact.last_name, email:contact.email||'', phone:contact.phone||'', address:contact.address||'', city:contact.city||'', state:contact.state||'NC', zip:contact.zip||'', notes:contact.notes||'' })
    setShowModal(true)
  }

  async function handleSave() {
    if (!form.first_name || !form.last_name) { notify('First and last name required'); return }
    const payload = { ...form, cpa_id: profile.id }
    if (editing) {
      await supabase.from('contacts').update(payload).eq('id', editing.id)
      notify('Contact updated!')
    } else {
      await supabase.from('contacts').insert(payload)
      notify('Contact created!')
    }
    setShowModal(false)
    load()
  }

  async function deleteContact(id) {
    if (!window.confirm('Delete this contact? Their linked entities will be unlinked but not deleted.')) return
    await supabase.from('clients').update({ contact_id: null }).eq('contact_id', id)
    await supabase.from('contacts').delete().eq('id', id)
    if (selectedContact?.id === id) setSelectedContact(null)
    load()
    notify('Contact deleted.')
  }

  async function linkEntity(clientId, contactId) {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      if (sessionError || !session?.access_token) throw new Error('Your session has expired. Please sign in again.')
      const res = await fetch('/.netlify/functions/link-contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ client_id: clientId, contact_id: contactId })
      })
      const data = await res.json()
      if (!res.ok || data.error) throw new Error(data.error || 'Failed to link entity.')
      await load()
      setShowLinkModal(false)
      notify('Entity linked!')
    } catch (err) { notify('Error: ' + (err.message || 'Failed to link entity.')) }
  }

  async function unlinkEntity(clientId) {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      if (sessionError || !session?.access_token) throw new Error('Your session has expired. Please sign in again.')
      const res = await fetch('/.netlify/functions/link-contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ client_id: clientId, contact_id: null })
      })
      const data = await res.json()
      if (!res.ok || data.error) throw new Error(data.error || 'Failed to unlink entity.')
      await load()
      notify('Entity unlinked.')
    } catch (err) { notify('Error: ' + (err.message || 'Failed to unlink entity.')) }
  }

  const filtered = contacts.filter(c =>
    `${c.first_name} ${c.last_name} ${c.email}`.toLowerCase().includes(search.toLowerCase())
  )

  const getEntities = contactId => clients.filter(c => c.contact_id === contactId)
  const unlinkedClients = clients.filter(c => !c.contact_id)

  const totalBilling = (contactId) => {
    // placeholder - would sum invoices in full impl
    return getEntities(contactId).length
  }

  if (loading) return <div>{[...Array(3)].map((_,i)=><div key={i} className="skeleton" style={{height:80,marginBottom:12}}/>)}</div>

  return (
    <div style={{display:'grid',gridTemplateColumns:selectedContact?'340px 1fr':'1fr',gap:20,alignItems:'start'}}>
      {toast && <div style={{position:'fixed',bottom:24,right:24,background:'#202326',color:'#CBB17B',padding:'12px 20px',borderRadius:10,fontSize:13,fontWeight:700,zIndex:9999,boxShadow:'0 8px 24px rgba(0,0,0,0.2)'}}>{toast}</div>}

      {/* Left panel - contact list */}
      <div>
        {/* Header */}
        <div className="card" style={{padding:'20px 22px',marginBottom:16,background:'#202326',border:'none'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
            <div>
              <h2 style={{color:'#fff',fontSize:18,marginBottom:2}}>Contacts</h2>
              <p style={{color:'rgba(255,255,255,0.5)',fontSize:12,margin:0}}>{contacts.length} contacts · {clients.filter(c=>c.contact_id).length} linked entities</p>
            </div>
            <button className="btn btn-primary" style={{fontSize:12}} onClick={openNew}>+ New Contact</button>
          </div>
          <input className="input" placeholder="Search contacts..." value={search} onChange={e=>setSearch(e.target.value)} style={{fontSize:13,background:'rgba(255,255,255,0.08)',border:'1px solid rgba(255,255,255,0.12)',color:'#fff'}} />
        </div>

        {/* Contact list */}
        <div className="card" style={{padding:0,overflow:'hidden'}}>
          {filtered.length===0 ? (
            <div style={{padding:40,textAlign:'center',color:'#62615D',fontSize:13}}>
              {search ? 'No contacts match your search.' : 'No contacts yet. Add your first contact to group multiple business entities under one person!'}
            </div>
          ) : filtered.map((contact,i) => {
            const entities = getEntities(contact.id)
            const isSelected = selectedContact?.id === contact.id
            return (
              <div key={contact.id}
                onClick={()=>setSelectedContact(isSelected?null:contact)}
                style={{padding:'14px 18px',display:'flex',alignItems:'center',gap:12,borderBottom:i<filtered.length-1?'1px solid var(--border)':'none',cursor:'pointer',background:isSelected?'#F0F9FF':'#fff',transition:'background 0.15s'}}
                onMouseEnter={e=>{if(!isSelected)e.currentTarget.style.background='#FAFAF8'}}
                onMouseLeave={e=>{if(!isSelected)e.currentTarget.style.background='#fff'}}>
                {/* Avatar */}
                <div style={{width:42,height:42,borderRadius:'50%',background:'#202326',display:'flex',alignItems:'center',justifyContent:'center',color:'#CBB17B',fontWeight:700,fontSize:15,flexShrink:0,fontFamily:"'DM Sans',sans-serif"}}>
                  {contact.first_name[0]}{contact.last_name[0]}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:14,fontWeight:700,color:'#202326'}}>{contact.first_name} {contact.last_name}</div>
                  {contact.email && <div style={{fontSize:11,color:'#62615D',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{contact.email}</div>}
                  <div style={{display:'flex',gap:6,marginTop:4,flexWrap:'wrap'}}>
                    {entities.length===0 ? (
                      <span style={{fontSize:10,color:'#9CA3AF'}}>No entities linked</span>
                    ) : entities.slice(0,3).map(e=>(
                      <span key={e.id} style={{fontSize:10,fontWeight:600,padding:'2px 7px',borderRadius:20,background:'#F0F9FF',color:'#1E40AF'}}>{e.business_name||e.full_name}</span>
                    ))}
                    {entities.length>3 && <span style={{fontSize:10,color:'#62615D'}}>+{entities.length-3} more</span>}
                  </div>
                </div>
                <div style={{flexShrink:0,display:'flex',gap:6}}>
                  <button onClick={e=>{e.stopPropagation();openEdit(contact)}} className="btn btn-ghost" style={{fontSize:11,padding:'5px 9px'}}>Edit</button>
                  <button onClick={e=>{e.stopPropagation();deleteContact(contact.id)}} className="btn btn-ghost" style={{fontSize:11,padding:'5px 9px',color:'#EF4444'}}>✕</button>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Right panel - contact detail */}
      {selectedContact && (
        <div>
          {/* Contact info card */}
          <div className="card" style={{padding:'22px 24px',marginBottom:16}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:16}}>
              <div style={{display:'flex',alignItems:'center',gap:14}}>
                <div style={{width:56,height:56,borderRadius:'50%',background:'#202326',display:'flex',alignItems:'center',justifyContent:'center',color:'#CBB17B',fontWeight:700,fontSize:20,fontFamily:"'DM Sans',sans-serif"}}>
                  {selectedContact.first_name[0]}{selectedContact.last_name[0]}
                </div>
                <div>
                  <div style={{fontSize:20,fontWeight:700,color:'#202326',fontFamily:"'DM Sans',sans-serif"}}>{selectedContact.first_name} {selectedContact.last_name}</div>
                  {selectedContact.email && <div style={{fontSize:13,color:'#62615D',marginTop:2}}>✉️ {selectedContact.email}</div>}
                  {selectedContact.phone && <div style={{fontSize:13,color:'#62615D'}}>📞 {selectedContact.phone}</div>}
                </div>
              </div>
              <button onClick={()=>openEdit(selectedContact)} className="btn btn-ghost" style={{fontSize:12}}>Edit Contact</button>
            </div>
            {(selectedContact.address||selectedContact.city) && (
              <div style={{fontSize:13,color:'#62615D',background:'#F7F3EB',borderRadius:8,padding:'10px 14px',marginBottom:12}}>
                📍 {[selectedContact.address,selectedContact.city,selectedContact.state,selectedContact.zip].filter(Boolean).join(', ')}
              </div>
            )}
            {selectedContact.notes && (
              <div style={{fontSize:13,color:'#62615D',fontStyle:'italic'}}>{selectedContact.notes}</div>
            )}
          </div>

          {/* Linked entities */}
          <div className="card" style={{padding:0,overflow:'hidden',marginBottom:16}}>
            <div style={{padding:'14px 18px',borderBottom:'1px solid var(--border)',display:'flex',justifyContent:'space-between',alignItems:'center',background:'#F7F3EB'}}>
              <div>
                <span style={{fontSize:14,fontWeight:700}}>Business Entities</span>
                <span style={{fontSize:12,color:'#62615D',marginLeft:8}}>{getEntities(selectedContact.id).length} linked</span>
              </div>
              <button onClick={()=>setShowLinkModal(true)} className="btn btn-ghost" style={{fontSize:12,color:'#CBB17B'}}>+ Link Entity</button>
            </div>

            {getEntities(selectedContact.id).length===0 ? (
              <div style={{padding:28,textAlign:'center',color:'#62615D',fontSize:13}}>
                No entities linked yet.<br />
                <button onClick={()=>setShowLinkModal(true)} style={{color:'#CBB17B',background:'none',border:'none',cursor:'pointer',fontSize:13,marginTop:8}}>+ Link an existing client entity</button>
              </div>
            ) : getEntities(selectedContact.id).map((entity,i)=>{
              const entities = getEntities(selectedContact.id)
              return (
                <div key={entity.id} style={{padding:'14px 18px',display:'flex',alignItems:'center',gap:12,borderBottom:i<entities.length-1?'1px solid var(--border)':'none'}}>
                  <div style={{width:36,height:36,borderRadius:9,background:'#F7F3EB',border:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:14,flexShrink:0}}>🏢</div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:700,color:'#202326'}}>{entity.business_name||entity.full_name}</div>
                    <div style={{display:'flex',gap:8,marginTop:3}}>
                      <span style={{fontSize:10,fontWeight:600,padding:'2px 7px',borderRadius:20,background:'#F0F9FF',color:'#1E40AF'}}>{ENTITY_LABELS[entity.entity_type]||entity.entity_type}</span>
                      {entity.status && <span style={{fontSize:10,fontWeight:600,padding:'2px 7px',borderRadius:20,background:'#F7F3EB',color:'#62615D'}}>{entity.status.replace('_',' ')}</span>}
                    </div>
                  </div>
                  {entity.email && <div style={{fontSize:11,color:'#62615D'}}>{entity.email}</div>}
                  <button onClick={()=>unlinkEntity(entity.id)} className="btn btn-ghost" style={{fontSize:11,padding:'5px 9px',color:'#EF4444',flexShrink:0}}>Unlink</button>
                </div>
              )
            })}
          </div>

          {/* Combined stats */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12}}>
            {[
              {label:'Total Entities', value:getEntities(selectedContact.id).length, icon:'🏢'},
              {label:'Entity Types', value:[...new Set(getEntities(selectedContact.id).map(e=>e.entity_type))].length, icon:'📋'},
              {label:'Tax Years', value:[...new Set(getEntities(selectedContact.id).map(e=>e.tax_year).filter(Boolean))].join(', ')||'—', icon:'📅'},
            ].map(s=>(
              <div key={s.label} className="card" style={{padding:'14px 16px',textAlign:'center'}}>
                <div style={{fontSize:22,marginBottom:6}}>{s.icon}</div>
                <div style={{fontSize:18,fontWeight:800,color:'#202326',fontFamily:"'DM Sans',sans-serif"}}>{s.value}</div>
                <div style={{fontSize:11,color:'#62615D'}}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Contact form modal */}
      {showModal && (
        <Modal title={editing?'Edit Contact':'New Contact'} onClose={()=>setShowModal(false)}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <div className="form-group"><label className="label">First Name *</label><input className="input" value={form.first_name} onChange={set('first_name')} placeholder="John" /></div>
            <div className="form-group"><label className="label">Last Name *</label><input className="input" value={form.last_name} onChange={set('last_name')} placeholder="Smith" /></div>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <div className="form-group"><label className="label">Email</label><input className="input" type="email" value={form.email} onChange={set('email')} placeholder="john@example.com" /></div>
            <div className="form-group"><label className="label">Phone</label><input className="input" type="tel" value={form.phone} onChange={set('phone')} placeholder="(252) 555-0100" /></div>
          </div>
          <div className="form-group"><label className="label">Address</label><input className="input" value={form.address} onChange={set('address')} placeholder="123 Main St" /></div>
          <div style={{display:'grid',gridTemplateColumns:'2fr 1fr 1fr',gap:12}}>
            <div className="form-group"><label className="label">City</label><input className="input" value={form.city} onChange={set('city')} placeholder="Roanoke Rapids" /></div>
            <div className="form-group"><label className="label">State</label><input className="input" value={form.state} onChange={set('state')} placeholder="NC" maxLength={2} /></div>
            <div className="form-group"><label className="label">ZIP</label><input className="input" value={form.zip} onChange={set('zip')} placeholder="27870" /></div>
          </div>
          <div className="form-group"><label className="label">Notes</label><textarea className="input" rows={3} value={form.notes} onChange={set('notes')} placeholder="Any notes about this contact..." /></div>
          <div style={{display:'flex',gap:10,marginTop:8}}>
            <button className="btn btn-ghost" onClick={()=>setShowModal(false)} style={{flex:1,justifyContent:'center'}}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} style={{flex:2,justifyContent:'center'}}>{editing?'Save Changes':'Create Contact'}</button>
          </div>
        </Modal>
      )}

      {/* Link entity modal */}
      {showLinkModal && selectedContact && (
        <Modal title={`Link Entity to ${selectedContact.first_name} ${selectedContact.last_name}`} onClose={()=>setShowLinkModal(false)}>
          <p style={{fontSize:13,color:'#62615D',marginBottom:16}}>Select an existing client entity to link to this contact. Only unlinked entities are shown.</p>
          {unlinkedClients.length===0 ? (
            <div style={{padding:24,textAlign:'center',color:'#62615D',fontSize:13}}>All client entities are already linked to contacts.</div>
          ) : (
            <div style={{display:'flex',flexDirection:'column',gap:8}}>
              {unlinkedClients.map(client=>(
                <div key={client.id} style={{display:'flex',alignItems:'center',gap:12,padding:'12px 14px',border:'1px solid var(--border)',borderRadius:10,cursor:'pointer',transition:'all 0.15s'}}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor='#CBB17B';e.currentTarget.style.background='#F1E8D7'}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--border)';e.currentTarget.style.background='#fff'}}>
                  <div style={{fontSize:20}}>🏢</div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:700,color:'#202326'}}>{client.business_name||client.full_name}</div>
                    <div style={{fontSize:11,color:'#62615D'}}>{ENTITY_LABELS[client.entity_type]||client.entity_type} · {client.email||'No email'}</div>
                  </div>
                  <button onClick={()=>linkEntity(client.id,selectedContact.id)} className="btn btn-primary" style={{fontSize:12,padding:'7px 14px'}}>Link</button>
                </div>
              ))}
            </div>
          )}
          <div style={{marginTop:16,padding:'12px 14px',background:'#F7F3EB',borderRadius:8,fontSize:12,color:'#62615D'}}>
            💡 To add a new entity, go to the Clients page and create a new client, then come back here to link it.
          </div>
        </Modal>
      )}
    </div>
  )
}
